import React, { useEffect, useMemo, useState } from 'react'
import logoCarsys from 'assets/svg/LogoCarsys.svg?url'
import logoCarcat from 'assets/svg/LogoCarcat.svg?url'
import { Theme } from '@cbgms/api/modules/enums'
import { useQueryParam } from '@cbgms/base/hooks/use-query-param/use-query-param'
import { useGetOnboardingTheme } from './hooks/use-get-onboarding-theme'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'

export interface IUnauthorizedUserThemeContextValue {
  applicationName: Theme
  environmentName: string
  theme: IUnauthorizedUserTheme
  Logo: () => JSX.Element
  optionalThemeUUID?: string // optional theme uuid, when passed in as query-param in the url
}

interface IUnauthorizedUserTheme {
  colors: {
    main: string
    accent: string
  }
  logo: string
}

interface Props {
  children: React.ReactNode
}

export const determineApplicationName = (url: string) => {
  const fr = /carcat/gi
  if (fr.test(url)) {
    return Theme.CarCat
  } else {
    return Theme.CarSys
  }
}

const determineEnvironmentName = (t: TFunction<'translation', undefined>, app: Theme) => {
  if (app === Theme.CarCat) return t('onboarding.application.env_name_portal')
  return t('onboarding.application.env_name_garage')
}

interface IBasicTheme {
  colors: {
    main: string
    accent: string
  }
  logo: string
}

const fallbackThemes = {
  [Theme.CarCat]: {
    colors: {
      main: '#044C94',
      accent: '#B4BC34'
    },
    logo: logoCarcat
  },
  [Theme.CarSys]: {
    colors: {
      main: '#005092',
      accent: '#e4051f'
    },
    logo: logoCarsys
  }
}

const defaultContextValue = {
  theme: fallbackThemes[Theme.CarSys],
  applicationName: Theme.CarSys,
  environmentName: 'garage',
  Logo: () => <DefaultLogo theme={fallbackThemes[Theme.CarSys]} />,
  optionalThemeUUID: undefined
}

export const UnauthorizedUserThemeContext = React.createContext<IUnauthorizedUserThemeContextValue>(defaultContextValue)

const DefaultLogo: React.FC<{ theme: IUnauthorizedUserTheme }> = ({ theme }) => {
  return (
    <Box>
      <Box
        component='img'
        sx={{
          height: '55px',
          marginBottom: '10px',
          verticalAlign: 'bottom'
        }}
        src={theme.logo}
      />
    </Box>
  )
}

export const UnauthorizedUserThemeContextProvider = (props: Props) => {
  const { children } = props
  const url = window.location.href
  const { t } = useTranslation()
  const [themeFromQueryParam] = useQueryParam('theme_uuid')
  const [optionalThemeUUID, setOptionalThemeUUID] = useState(themeFromQueryParam)
  // set default theme using query param, make sure not to reset when theme_uuid is not in query param anymore
  useEffect(() => {
    if (!optionalThemeUUID && themeFromQueryParam) {
      setOptionalThemeUUID(themeFromQueryParam)
    }
  }, [optionalThemeUUID, themeFromQueryParam])
  const { data } = useGetOnboardingTheme(optionalThemeUUID)

  // resolve theme from url
  const applicationName = useMemo(() => determineApplicationName(url), [url])
  const environmentName = useMemo(() => determineEnvironmentName(t, applicationName), [t, applicationName])
  const theme = useMemo<IBasicTheme>(() => {
    if (data?.Data) {
      // we loaded a theme
      return {
        colors: {
          main: data.Data.MainColour,
          accent: fallbackThemes[applicationName].colors.accent // There is no accent colours on the theme yet, use the fallback one
        },
        logo: data.Data.LogoURL
      }
    }
    return fallbackThemes[applicationName]
  }, [applicationName, data?.Data])
  const Logo = useMemo(() => () => <DefaultLogo theme={theme} />, [theme])

  const contextValue = useMemo(
    () => ({ applicationName, theme, Logo, optionalThemeUUID, environmentName }),
    [applicationName, theme, Logo, optionalThemeUUID, environmentName]
  )
  return <UnauthorizedUserThemeContext.Provider value={contextValue}>{children}</UnauthorizedUserThemeContext.Provider>
}

export function useUnauthorizedUserThemeContext() {
  const context = React.useContext(UnauthorizedUserThemeContext)
  return context as IUnauthorizedUserThemeContextValue
}
